import {
  ApplicationRef,
  ComponentFactoryResolver,
  Injectable,
  Injector,
  NgZone,
} from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ClaimDetailPdfComponent } from './claim-detail-pdf/claim-detail-pdf.component';

@Injectable({
  providedIn: 'root',
})
export class ClaimPdfExportService {
  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
    private componentFactoryResolver: ComponentFactoryResolver,
    private ngZone: NgZone,
  ) {}

  async exportClaimDetail(vo: any, fileName: string): Promise<void> {
    return this.ngZone.run(async () => {
      const host = document.createElement('div');
      // 保持在视口内(0,0)渲染以保证布局正确，避免离屏截图导致文字与背景层叠错位；
      // 用 opacity:0 对用户完全隐藏（html2canvas 克隆目标元素单独渲染，不受祖先 opacity 影响），
      // 防止导出过程中 PDF 预览在页面上短暂闪现。
      host.style.cssText =
        'position:fixed;left:0;top:0;z-index:-1;pointer-events:none;overflow:visible;opacity:0;';
      document.body.appendChild(host);

      const factory =
        this.componentFactoryResolver.resolveComponentFactory(ClaimDetailPdfComponent);
      const componentRef = factory.create(this.injector, [], host);
      this.appRef.attachView(componentRef.hostView);
      const pdfComponent = componentRef.instance as ClaimDetailPdfComponent;

      try {
        pdfComponent.applyClaimDetail(vo);
        componentRef.changeDetectorRef.detectChanges();
        await this.waitForRender();

        const target = host.querySelector('.create-claim.claim-detail') as HTMLElement | null;
        if (!target) {
          throw new Error('PDF render root not found');
        }

        const renderedW = target.scrollWidth;
        const pdfProbe = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW = pdfProbe.internal.pageSize.getWidth();
        const pageH = pdfProbe.internal.pageSize.getHeight();
        const pageHeightPx = (pageH * renderedW) / pageW;

        this.insertSmartPageBreaks(target, pageHeightPx);
        await this.waitForRender();
        this.forceClaimQtyColor(target);
        await this.waitForRender();

        const canvas = await html2canvas(target, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#EFF1F2',
          windowWidth: renderedW,
          width: renderedW,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const imgH = (canvas.height / canvas.width) * pageW;

        let yOffset = 0;
        let remaining = imgH;
        while (remaining > 0) {
          if (yOffset > 0) {
            pdf.addPage();
          }
          pdf.addImage(imgData, 'JPEG', 0, -yOffset, pageW, imgH);
          yOffset += pageH;
          remaining -= pageH;
        }

        pdf.save(fileName);
      } finally {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
        document.body.removeChild(host);
      }
    });
  }

  private waitForRender(): Promise<void> {
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTimeout(resolve, 200));
      });
    });
  }

  /**
   * 截图前检测跨页元素，插入占位将其推到下一页，避免行/块被固定高度裁切。
   */
  private insertSmartPageBreaks(root: HTMLElement, pageHeightPx: number): void {
    const selectors = [
      'section.section',
      '.section-title',
      '.section-header-row',
      '.table-wrapper thead',
      '.table-wrapper tbody tr:not(.pdf-page-break-spacer)',
      '.attachment-item',
      '.remark-row',
    ];

    const maxPasses = 300;
    for (let pass = 0; pass < maxPasses; pass++) {
      const elements = this.collectBreakableElements(root, selectors);
      const rootTop = root.getBoundingClientRect().top;
      let inserted = false;

      for (const el of elements) {
        const rect = el.getBoundingClientRect();
        const top = rect.top - rootTop;
        const height = rect.height;

        if (height <= 0 || height >= pageHeightPx) {
          continue;
        }

        const pageBottom = (Math.floor(top / pageHeightPx) + 1) * pageHeightPx;
        if (top < pageBottom && top + height > pageBottom) {
          this.insertPageBreakSpacer(el, pageBottom - top);
          inserted = true;
          break;
        }
      }

      if (!inserted) {
        break;
      }
    }
  }

  private collectBreakableElements(root: HTMLElement, selectors: string[]): HTMLElement[] {
    const seen = new Set<HTMLElement>();
    const elements: HTMLElement[] = [];

    selectors.forEach(selector => {
      root.querySelectorAll<HTMLElement>(selector).forEach(el => {
        if (!seen.has(el)) {
          seen.add(el);
          elements.push(el);
        }
      });
    });

    return elements.sort((a, b) => {
      const rectA = a.getBoundingClientRect();
      const rectB = b.getBoundingClientRect();
      return rectA.top !== rectB.top ? rectA.top - rectB.top : rectA.left - rectB.left;
    });
  }

  private insertPageBreakSpacer(el: HTMLElement, heightPx: number): void {
    if (heightPx <= 0) {
      return;
    }

    const bg = '#EFF1F2';

    if (el.parentElement?.tagName === 'TBODY') {
      const row = document.createElement('tr');
      row.className = 'pdf-page-break-spacer';
      const cell = document.createElement('td');
      cell.colSpan = 99;
      cell.style.cssText = [
        `height:${heightPx}px`,
        'padding:0',
        'border:none',
        `background:${bg}`,
        'line-height:0',
        'font-size:0',
      ].join(';');
      row.appendChild(cell);
      el.parentElement.insertBefore(row, el);
      return;
    }

    const tableScoped =
      el.tagName === 'THEAD' ? (el.closest('.table-wrapper') as HTMLElement | null) : null;
    const anchor = tableScoped ?? el;

    const spacer = document.createElement('div');
    spacer.className = 'pdf-page-break-spacer';
    spacer.style.cssText = `height:${heightPx}px;width:100%;background:${bg};flex-shrink:0;`;
    anchor.parentElement?.insertBefore(spacer, anchor);
  }

  private forceClaimQtyColor(root: HTMLElement): void {
    const claimQtyCells = root.querySelectorAll<HTMLElement>('td.claim-qty-value');
    claimQtyCells.forEach(cell => {
      cell.style.setProperty('color', '#007bc0', 'important');
      cell.style.setProperty('-webkit-text-fill-color', '#007bc0', 'important');

      const valueText = (cell.textContent || '').trim();
      cell.textContent = '';

      const textSpan = document.createElement('span');
      textSpan.textContent = valueText;
      textSpan.style.setProperty('color', '#007bc0', 'important');
      textSpan.style.setProperty('-webkit-text-fill-color', '#007bc0', 'important');
      cell.appendChild(textSpan);
    });
  }
}
